import React from 'react'
import { Button } from './ui/button'
import { Card, CardContent, CardHeader, CardTitle } from './ui/card'
import { Input } from './ui/input'
import { Checkbox } from './ui/checkbox'
import { Label } from './ui/label'
import { Badge } from './ui/badge'
import { Copy, Mail, Link as LinkIcon, Video, Sparkles, Printer } from 'lucide-react'
import { ChevronRightIcon, DocumentIcon } from '@heroicons/react/24/outline'
import { toast } from 'react-hot-toast'
import EmailTemplateManager from './EmailTemplateManager'

/**
 * QuoteSharingStep (Step 5)
 * Handles: Passwortschutz, Share-Link, E-Mail Template, Quick Actions
 */
const QuoteSharingStep = ({
  savedQuoteId,
  savedQuoteNumber,
  savedViewHash,
  passwordProtected, setPasswordProtected,
  customPassword, setCustomPassword,
  selectedEmailTemplate, setSelectedEmailTemplate,
  activeCompany,
  customerInfo,
  companyId,
  calculatePricing,
  onSave,
  onBack,
}) => {
  if (!savedQuoteId) {
    return (
      <div className="text-center py-12 text-slate-500">
        <p className="text-lg font-semibold mb-2">Angebot noch nicht gespeichert</p>
        <p className="text-sm">Bitte speichere das Angebot zuerst, um es zu teilen.</p>
        {onSave && (
          <Button onClick={onSave} className="mt-4 bg-blue-600 hover:bg-blue-700 text-white">
            Angebot speichern
          </Button>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-6">
{/* Step 5: Teilen */}
{activeCompany && customerInfo?.company && (
  <Card>
    <CardHeader>
      <CardTitle className="flex items-center text-slate-800">
        <Sparkles className="h-6 w-6 mr-2 text-blue-600" />
        Schritt 5: Angebot teilen
      </CardTitle>
      <p className="text-slate-600 mt-2">Teilen Sie Ihr Angebot mit dem Kunden per Link oder E-Mail.</p>
          
          {/* Passwortschutz Toggle */}
          <div className="mb-4 p-4 bg-white rounded-lg border border-blue-200">
            <div className="flex items-center justify-between mb-3">
              <div>
                <label className="font-medium text-slate-800 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={passwordProtected}
                    onChange={(e) => setPasswordProtected(e.target.checked)}
                    className="mr-3 w-4 h-4 rounded"
                  />
                  Passwortschutz aktivieren
                </label>
                <p className="text-xs text-slate-600 mt-1 ml-7">
                  Kunde muss Passwort eingeben, um das Angebot zu sehen
                </p>
              </div>
            </div>
            
            {passwordProtected && (
              <div className="pt-3 border-t">
                <label className="text-sm font-medium text-slate-700 mb-2 block">
                  Eigenes Passwort (optional)
                </label>
                <Input
                  type="text"
                  value={customPassword}
                  onChange={(e) => setCustomPassword(e.target.value)}
                  placeholder="Leer lassen = PLZ-Schutz"
                  className="w-full"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Leer = Kunde nutzt PLZ | Ausgefüllt = Ihr eigenes Passwort
                </p>
              </div>
            )}
          </div>

      
      {/* Action Buttons - wie in Schritt 4 */}
      <div className="mt-4 flex flex-wrap gap-3">
        <Button 
          onClick={onBack}
          className="flex items-center gap-2"
          disabled={!savedQuoteId}
        >
          <Printer className="w-4 h-4" />
          Als PDF drucken {!savedQuoteId && '(Angebot zuerst speichern)'}
        </Button>
        
        <Button 
          onClick={() => {
            if (savedViewHash) {
              navigator.clipboard.writeText(`${window.location.origin}/quote/view/${savedViewHash}`)
              toast.success('Link kopiert!')
            }
          }}
          variant="outline"
          className="flex items-center gap-2" 
          disabled={!savedViewHash}
        >
          <LinkIcon className="w-4 h-4" />
          Link kopieren {!savedViewHash && '(Angebot zuerst speichern)'}
        </Button>
        
        <Button 
          onClick={() => {
            if (savedViewHash) {
              window.open(`${window.location.origin}/quote/view/${savedViewHash}`, '_blank')
            }
          }}
          variant="outline"
          className="flex items-center gap-2" 
          disabled={!savedViewHash}
        >
          <ChevronRightIcon className="w-4 h-4" />
          Link im neuen Tab öffnen {!savedViewHash && '(Angebot zuerst speichern)'}
        </Button>
        
        <Button 
          variant="outline" 
          className="flex items-center gap-2 bg-gray-100 border-gray-300 text-gray-500 cursor-not-allowed"
          disabled
        >
          <Video className="w-4 h-4" />
          Video-Erklärung (Bald verfügbar)
        </Button>
      </div>
      
      {/* Speichern & Zurück Buttons */}
      <div className="mt-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <Button
            onClick={onBack}
            variant="outline"
            className="border-slate-300 text-slate-700 hover:bg-slate-50"
          >
            <ChevronRightIcon className="h-4 w-4 mr-2 transform rotate-180" />
            Zurück zu Schritt 4
          </Button>
          <Button
            onClick={onSave}
            className="bg-green-600 hover:bg-green-700 text-white"
          >
            💾 Angebot speichern
          </Button>
        </div>
      </div>
    </CardHeader>
    <CardContent>
      <div className="space-y-6">
        {/* Quote Link Section */}
        <div className="p-6 bg-blue-50 rounded-lg border border-blue-200">
          <h3 className="text-lg font-semibold text-blue-800 mb-3 flex items-center">
            <DocumentIcon className="h-5 w-5 mr-2" />
            Angebots-Link
          </h3>
          {savedViewHash ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Input
                  readOnly
                  value={`${window.location.origin}/quote/view/${savedViewHash}`}
                  className="flex-1 bg-white"
                />
                <Button
                  onClick={() => {
                    navigator.clipboard.writeText(`${window.location.origin}/quote/view/${savedViewHash}`)
                    toast.success('Link kopiert!')
                  }}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  Kopieren
                </Button>
                <Button
                  onClick={() => window.open(`${window.location.origin}/quote/view/${savedViewHash}`, '_blank')}
                  variant="outline"
                  className="border-blue-600 text-blue-700 hover:bg-blue-50"
                >
                  Im neuen Tab öffnen
                </Button>
              </div>
              <p className="text-sm text-blue-700">
                Dieser Link führt zur interaktiven Ansicht des Angebots, wo der Kunde es annehmen kann.
              </p>
            </div>
          ) : (
            <div className="text-amber-700 bg-amber-50 p-3 rounded-lg border border-amber-200">
              <p className="font-medium">Angebot noch nicht gespeichert</p>
              <p className="text-sm mt-1">Bitte speichern Sie das Angebot zuerst in Schritt 4, um einen Link zu generieren.</p>
            </div>
          )}
        </div>

        {/* Email Template Section */}
        <div className="p-6 bg-green-50 rounded-lg border border-green-200">
          <EmailTemplateManager
            companyId={activeCompany?.id}
            companyInfo={activeCompany}
            customerInfo={customerInfo}
            quoteNumber={savedQuoteNumber || savedQuoteId}
            viewUrl={savedViewHash ? `${window.location.origin}/quote/view/${savedViewHash}` : null}
            calculatePricing={calculatePricing}
            onSelectTemplate={setSelectedEmailTemplate}
            passwordProtected={passwordProtected}
            customPassword={customPassword}
          />
        </div>

        {/* Quick Actions */}
        <div className="flex flex-wrap gap-3">
          {customerInfo.email && (
            <Button
              onClick={() => {
                const subject = encodeURIComponent(`Ihr Angebot von ${activeCompany?.name || 'uns'}`)
                const body = encodeURIComponent(`Sehr geehrte(r) ${customerInfo.name},\n\nvielen Dank für Ihr Interesse. Ihr Angebot finden Sie unter:\n${savedViewHash ? `${window.location.origin}/quote/view/${savedViewHash}` : '[Link]'}\n\nMit freundlichen Grüßen`)
                window.open(`mailto:${customerInfo.email}?subject=${subject}&body=${body}`)
              }}
              variant="outline"
            >
              E-Mail öffnen
            </Button>
          )}
          <Button
            onClick={onBack}
            variant="outline"
          >
            PDF nochmal ansehen
          </Button>
        </div>
      </div>
    </CardContent>
  </Card>
)}

    </div>
  )
}

export default QuoteSharingStep
